import {useAuth} from '@apps-common/auth-react';
import {useCallback, useEffect, useRef, useState} from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

const rootUrl = import.meta.env.VITE_ROOT || 'http://localhost:8000/';

function getResourceUrl(href) {
  const resource = new URL(href, rootUrl);
  return resource.href;
}

function withToken(token) {
  return function (url, options = {}) {
    const headers = {
      ...options.headers,
      authorization: token ? `Bearer ${token}` : null,
    };
    return fetch(url, {...options, headers});
  };
}

function useFetch() {
  const auth = useAuth();
  return withToken(auth.user?.accessToken);
}

function useFetchJSON(key, transform) {
  const fetcher = useFetch();

  return async function (url, options = {}) {
    const headers = {...options.headers, 'content-type': 'application/json'};
    const response = await fetcher(url, {
      ...options,
      body: options.body ? JSON.stringify(options.body) : null,
      headers,
    });
    if (!response.ok) {
      let message;
      try {
        const body = await response.json();
        if (body && body.message) {
          message = body.message;
        }
      } catch {
        // pass
      }
      if (!message) {
        message = `Unexpected response: ${response.statusText}`;
      }
      throw new Error(message);
    }
    if (response.status === 204) {
      return null;
    }
    const body = await response.json();
    const data = key ? body[key] : body;
    return transform ? transform(data) : data;
  };
}

function useFetchBlob() {
  const fetcher = useFetch();

  return async function (url, options = {}) {
    const headers = {
      ...options.headers,
      'content-type': 'application/octet-stream',
    };
    const response = await fetcher(url, {...options, headers});
    if (response.headers.get('content-type').startsWith('application/json')) {
      return response.json();
    }
    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`,
      );
    }
    return response.blob();
  };
}

export function useRequest(fetcher, url, method) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    data: null,
  });

  const request = useCallback(
    async body => {
      if (!url) {
        return;
      }
      setState({loading: true, error: null, data: null});
      let data;
      try {
        data = await fetcher(url, {method, body});
      } catch (error) {
        setState({loading: false, error, data: null});
        return;
      }
      setState({loading: false, error: null, data});
    },
    [fetcher, method, url],
  );

  return {...state, request};
}

function useResource(url, fetcher) {
  const auth = useAuth();
  return useSWR([url, auth.user], () => fetcher(url));
}

export function usePublication(id) {
  const fetcher = useFetchJSON();
  return useResource(getResourceUrl(`./publications/${id}`), fetcher);
}

export function usePermissions() {
  const fetcher = useFetchJSON();
  return useResource(getResourceUrl('./permissions'), fetcher);
}

export function useDownload(url) {
  const fetcher = useFetchBlob();
  return useRequest(fetcher, getResourceUrl(url), 'GET');
}

export function useUploadAttachment() {
  const url = getResourceUrl('./attachments');
  const fetcher = useFetchBlob();
  return useRequest(fetcher, url, 'POST');
}

export function useTopics() {
  const fetcher = useFetchJSON('data', data => data.sort());
  return useResource(getResourceUrl('./topics'), fetcher);
}

export function useDataTypes() {
  const fetcher = useFetchJSON('data', data => data.sort());
  return useResource(getResourceUrl('./data-types'), fetcher);
}

export function usePublicationTypes() {
  const fetcher = useFetchJSON('data', data => data.sort());
  return useResource(getResourceUrl('./publication-types'), fetcher);
}

export function useKeywords() {
  const fetcher = useFetchJSON('data', data => data.sort());
  return useResource(getResourceUrl('./keywords'), fetcher);
}

export function useCreatePublication() {
  const url = getResourceUrl('./publications');
  const fetcher = useFetchJSON();
  return useRequest(fetcher, url, 'POST');
}

export function useUpdatePublication(id) {
  const url = getResourceUrl(`./publications/${id}`);
  const fetcher = useFetchJSON();
  return useRequest(fetcher, url, 'PUT');
}

export function useDeletePublication(id) {
  const url = getResourceUrl(`./publications/${id}`);
  const fetcher = useFetchJSON();
  return useRequest(fetcher, url, 'DELETE');
}

export function getDownloadPublicationsUrl(token, query) {
  const params = new URLSearchParams(query);
  params.set('download', 'true');
  params.set('token', token);
  return getResourceUrl(`./publications?${params}`);
}

function getPublicationsUrl(query, user) {
  return function (pageIndex, previous) {
    if (pageIndex === 0) {
      const url = new URL(
        getResourceUrl('./publications' + (query ? `?${query}` : '')),
      );
      url.searchParams.set('limit', 100);
      return [url.toString(), user];
    }
    if (!previous.more) {
      return [null, user];
    }
    for (const link of previous.links) {
      if (link.rel === 'next') {
        return [link.href, user];
      }
    }
  };
}

export function usePublications(query) {
  const fetcher = useFetchJSON();
  const queryRef = useRef(query);
  const publicationsRef = useRef([]);
  const auth = useAuth();

  const {data, error, size, setSize} = useSWRInfinite(
    getPublicationsUrl(query, auth.user),
    ([url]) => fetcher(url),
  );

  useEffect(() => {
    if (queryRef.current !== query) {
      queryRef.current = query;
      setSize(1);
    }
  });

  let more;
  let count = 0;
  if (data && data.length === size) {
    let morePages = false;
    const publications = publicationsRef.current;
    publications.length = 0;
    for (const page of data) {
      if (!count) {
        count = page.count;
      }
      for (const item of page.data) {
        publications.push(item);
      }
      morePages = page.more;
    }
    if (morePages) {
      more = () => {
        setSize(size + 1);
      };
    }
  }

  const loading = (data && data.length !== size) || (!data && !error);

  return {
    data: publicationsRef.current,
    count,
    error,
    more,
    loading,
  };
}
